"use client";

import { useState, useEffect, useRef } from "react";
import { useGSAP } from "@gsap/react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import gsap from "gsap";
import AnimatedHandwriting from "./animated-handwriting";
import { useHomeIntro } from "@/contexts/home-intro-context";
import CTAButton from "@/components/ui/cta-button";
import PageLoadingSpinner from "@/components/ui/page-loading-spinner";
import Footer from "@/components/global/footer";

interface HomeClientProps {
  videoUrl?: string;
  hevcVideoUrl?: string;
  webmVideoUrl?: string;
  posterUrl?: string;
  menuImages?: {
    aboutImage?: string;
    collectionImage?: string;
  };
  extinctLogo?: string;
}

export default function HomeClient({
  videoUrl,
  hevcVideoUrl,
  webmVideoUrl,
  posterUrl,
  menuImages,
  extinctLogo,
}: HomeClientProps) {
  const [isExiting, setIsExiting] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const isSkippingRef = useRef(false);
  const router = useRouter();
  const containerRef = useRef<HTMLDivElement>(null);
  const linksRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const collectionImageRef = useRef<HTMLImageElement>(null);
  const aboutImageRef = useRef<HTMLImageElement>(null);
  const collectionTextRef = useRef<HTMLDivElement>(null);
  const aboutTextRef = useRef<HTMLDivElement>(null);
  // Individual text element refs for staggered animations
  const collectionViewTheRef = useRef<HTMLSpanElement>(null);
  const collectionHeadingRef = useRef<HTMLSpanElement>(null);
  const collectionCtaRef = useRef<HTMLDivElement>(null);
  const aboutViewTheRef = useRef<HTMLSpanElement>(null);
  const aboutHeadingRef = useRef<HTMLSpanElement>(null);
  const aboutCtaRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const { setShowHomeNav, setMenuImages, hasClickedIntro, setHasClickedIntro } =
    useHomeIntro();
  const hasAnimatedEntranceRef = useRef(false);

  // Initialize state based on context - skip intro if already clicked
  const [videoEnded, setVideoEnded] = useState(hasClickedIntro);
  const [introComplete, setIntroComplete] = useState(hasClickedIntro);
  const [linksReady, setLinksReady] = useState(hasClickedIntro);
  const [fontsReady, setFontsReady] = useState(false);

  // Ensure fonts are loaded before animations to prevent layout shifts
  useEffect(() => {
    document.fonts.ready.then(() => setFontsReady(true));
  }, []);

  // Handle returning to home page when intro was already seen
  useEffect(() => {
    if (hasClickedIntro && containerRef.current) {
      // Hide video container immediately
      containerRef.current.style.display = "none";
      // Show nav
      setShowHomeNav(true);
      // Make cards visible immediately (no animation)
      if (linksRef.current) {
        linksRef.current.classList.remove("invisible");
        linksRef.current.style.opacity = "1";
        linksRef.current.style.transform = "translateY(0)";
      }
      // Make all card elements visible
      [
        collectionImageRef,
        collectionViewTheRef,
        collectionHeadingRef,
        collectionCtaRef,
        aboutImageRef,
        aboutViewTheRef,
        aboutHeadingRef,
        aboutCtaRef,
        footerRef,
      ].forEach((ref) => {
        if (ref.current) {
          ref.current.style.opacity = "1";
        }
      });
      hasAnimatedEntranceRef.current = true;
    }
  }, []); // Run once on mount

  // Set menu images in context when component mounts
  useEffect(() => {
    if (menuImages) {
      setMenuImages(menuImages);
    }
  }, [menuImages, setMenuImages]);

  // Preload menu images during intro so they're ready for fade-in
  useEffect(() => {
    if (menuImages?.collectionImage) {
      const img1 = new Image();
      img1.src = menuImages.collectionImage;
    }
    if (menuImages?.aboutImage) {
      const img2 = new Image();
      img2.src = menuImages.aboutImage;
    }
  }, [menuImages]);

  // Preload collection page after animations complete (delayed to avoid competing with entrance animations)
  useEffect(() => {
    if (videoEnded) {
      // Wait for entrance animations to complete before prefetching
      const timeoutId = setTimeout(() => {
        const doPrefetch = () => {
          const link = document.createElement("link");
          link.rel = "prefetch";
          link.href = "/collection";
          document.head.appendChild(link);
        };

        // Use requestIdleCallback if available (not in Safari), otherwise just run
        if (typeof requestIdleCallback !== "undefined") {
          requestIdleCallback(doPrefetch, { timeout: 3000 });
        } else {
          doPrefetch();
        }
      }, 1500); // Delay until card entrance animations are done

      return () => clearTimeout(timeoutId);
    }
  }, [videoEnded]);

  // Function to trigger card entrance animations
  const triggerCardEntrance = (
    isSkipping: boolean,
    isAlreadyComplete: boolean,
  ) => {
    if (hasAnimatedEntranceRef.current) return;
    hasAnimatedEntranceRef.current = true;

    // Restored Thursday's timing values - gives browser breathing room
    const cardStartDelay = isSkipping ? 0.1 : isAlreadyComplete ? 0.1 : 0.3;
    const textStagger = 0.15;
    const baseDelay = isSkipping ? 0.2 : 0.5;
    const cardOffset = isSkipping ? 0.3 : 0.5;

    // Use requestAnimationFrame to let browser paint before animating
    requestAnimationFrame(() => {
      if (linksRef.current) {
        // Ensure element is visible before animating
        linksRef.current.classList.remove("invisible");
        gsap.to(linksRef.current, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          delay: cardStartDelay,
          ease: "power2.out",
        });
      }

      const isDesktop =
        typeof window !== "undefined" &&
        window.matchMedia("(min-width: 768px)").matches;

      const sequence = isDesktop
        ? [
            { el: aboutImageRef.current, x: 0, delay: baseDelay },
            { el: aboutViewTheRef.current, x: -50, delay: baseDelay },
            {
              el: aboutHeadingRef.current,
              x: -50,
              delay: baseDelay + textStagger,
            },
            {
              el: aboutCtaRef.current,
              x: -50,
              delay: baseDelay + textStagger * 2,
            },
            {
              el: collectionImageRef.current,
              x: 0,
              delay: baseDelay + cardOffset,
            },
            {
              el: collectionViewTheRef.current,
              x: 50,
              delay: baseDelay + cardOffset,
            },
            {
              el: collectionHeadingRef.current,
              x: 50,
              delay: baseDelay + cardOffset + textStagger,
            },
            {
              el: collectionCtaRef.current,
              x: 50,
              delay: baseDelay + cardOffset + textStagger * 2,
            },
          ]
        : [
            { el: collectionImageRef.current, x: 0, delay: baseDelay },
            { el: collectionViewTheRef.current, x: 50, delay: baseDelay },
            {
              el: collectionHeadingRef.current,
              x: 50,
              delay: baseDelay + textStagger,
            },
            {
              el: collectionCtaRef.current,
              x: 50,
              delay: baseDelay + textStagger * 2,
            },
            {
              el: aboutImageRef.current,
              x: 0,
              delay: baseDelay + cardOffset + 0.1,
            },
            {
              el: aboutViewTheRef.current,
              x: -50,
              delay: baseDelay + cardOffset + 0.1,
            },
            {
              el: aboutHeadingRef.current,
              x: -50,
              delay: baseDelay + cardOffset + 0.1 + textStagger,
            },
            {
              el: aboutCtaRef.current,
              x: -50,
              delay: baseDelay + cardOffset + 0.1 + textStagger * 2,
            },
          ];

      sequence.forEach(({ el, x, delay }) => {
        if (el) {
          gsap.fromTo(
            el,
            { opacity: 0, x: x },
            {
              opacity: 1,
              x: 0,
              duration: 0.8,
              delay,
              ease: "power2.out",
            },
          );
        }
      });

      // Animate footer in with the cards
      if (footerRef.current) {
        gsap.fromTo(
          footerRef.current,
          { opacity: 0, y: 20 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: cardStartDelay,
            ease: "power2.out",
          },
        );
      }
    }); // End requestAnimationFrame
  };

  // Function to trigger fade to menu
  const handleIntroComplete = (skipped = false) => {
    if (introComplete) return; // Prevent multiple triggers

    const isSkipping = skipped;
    const isAlreadyComplete = hasClickedIntro && !isSkipping;
    // Restored Thursday's fade duration - gives browser time to process
    const fadeDuration = isSkipping ? 0.4 : isAlreadyComplete ? 0 : 1;

    // 1. Immediate Visual Actions (Direct GSAP)
    if (containerRef.current) {
      // Kill any active animations (like handwriting delay/timeline)
      gsap.killTweensOf(containerRef.current);
      const children = containerRef.current.querySelectorAll("*");
      children.forEach((child) => gsap.killTweensOf(child));

      // Disable interaction
      gsap.set(containerRef.current, { pointerEvents: "none" });

      // Fade out video container
      gsap.to(containerRef.current, {
        opacity: 0,
        duration: fadeDuration,
        ease: "power2.inOut",
        onComplete: () => {
          if (containerRef.current) {
            containerRef.current.style.display = "none";
          }
        },
      });
    }

    if (videoRef.current) {
      videoRef.current.pause();
    }

    // 2. State Updates for React (do these immediately)
    if (skipped) isSkippingRef.current = true;
    setIntroComplete(true);
    setVideoEnded(true);
    setHasClickedIntro(true);
    setShowHomeNav(true);
    setLinksReady(true);

    // 3. Trigger card entrance only after fonts are ready to prevent layout shifts
    const startCardAnimation = () => {
      triggerCardEntrance(isSkipping, isAlreadyComplete);
    };

    if (fontsReady) {
      startCardAnimation();
    } else {
      // Wait for fonts, but with a timeout fallback
      document.fonts.ready.then(startCardAnimation);
    }
  };

  // Function to animate cards out and navigate
  const animateCardsOut = (targetUrl: string) => {
    if (isExiting) return;
    setIsExiting(true);
    setIsNavigating(true);

    const textStagger = 0.1;

    // Both cards animate out simultaneously - reverse order
    const exitSequence = [
      // Collection card exits to the right
      { el: collectionCtaRef.current, x: 50, delay: 0 },
      { el: collectionHeadingRef.current, x: 50, delay: textStagger },
      { el: collectionViewTheRef.current, x: 50, delay: textStagger * 2 },
      { el: collectionImageRef.current, x: 0, delay: textStagger * 2.5 },
      // About card exits to the left
      { el: aboutCtaRef.current, x: -50, delay: 0 },
      { el: aboutHeadingRef.current, x: -50, delay: textStagger },
      { el: aboutViewTheRef.current, x: -50, delay: textStagger * 2 },
      { el: aboutImageRef.current, x: 0, delay: textStagger * 2.5 },
    ];

    exitSequence.forEach(({ el, x, delay }) => {
      if (el) {
        gsap.to(el, {
          opacity: 0,
          x: x,
          duration: 0.5,
          delay,
          ease: "power2.in",
        });
      }
    });

    // Navigate after animation completes
    setTimeout(() => {
      router.push(targetUrl);
    }, 550);
  };

  // Handle case where intro was already seen in this session
  useGSAP(
    () => {
      if (
        hasClickedIntro &&
        containerRef.current &&
        !introComplete &&
        !hasAnimatedEntranceRef.current
      ) {
        handleIntroComplete(false);
      }
    },
    { dependencies: [hasClickedIntro] },
  );

  return (
    <div className="relative min-h-screen w-full">
      {/* VIDEO CONTAINER - Placed outside the grid for true viewport centering on mobile */}
      <div
        ref={containerRef}
        // Fixed to viewport on mobile, positioned within grid on desktop
        className="fixed inset-0 flex items-center justify-center z-50 cursor-pointer md:z-50 -translate-y-[5%]"
        onClick={() => handleIntroComplete(true)}
        aria-hidden={introComplete}
        inert={introComplete ? true : undefined}
      >
        <div
          className="relative w-auto max-h-[70vh] md:max-h-none md:w-full max-w-[1100px] px-[10px] md:px-[15px]"
          style={{ aspectRatio: "1537/1126" }}
        >
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            poster={posterUrl}
            onEnded={() => handleIntroComplete(false)}
            className="w-full h-full object-contain"
          >
            {/* Safari/iOS (HEVC with Alpha) - Must be first for Safari to pick it up */}
            {hevcVideoUrl && (
              <source
                src={hevcVideoUrl}
                type='video/quicktime; codecs="hvc1"'
              />
            )}
            {/* Chrome/Firefox (WebM VP9 with Alpha) */}
            {webmVideoUrl && (
              <source src={webmVideoUrl} type="video/webm; codecs=vp9" />
            )}
            {/* Fallback - only used if neither alpha format is supported */}
            {videoUrl && <source src={videoUrl} type="video/mp4" />}
          </video>
          {/* Handwriting overlay */}
          {!introComplete && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20 px-[16.67%] md:px-0 -translate-y-[20%] md:translate-y-0">
              <AnimatedHandwriting onComplete={handleIntroComplete} />
            </div>
          )}
        </div>
      </div>

      <div
        className={`main-grid-container !px-0 md:!px-[15px] md:items-center md:min-h-screen relative w-full ${!linksReady ? "pointer-events-none" : ""}`}
      >
        {/* NAVIGATION LINKS */}
        <div
          ref={linksRef}
          // Hidden during intro to prevent layout issues, revealed when video ends
          className={`relative md:absolute md:inset-0 grid grid-cols-[repeat(6,minmax(0,1fr))] gap-y-4 px-0 pt-0 pb-12 md:pt-0 md:pb-0 md:grid-cols-12 md:gap-[15px] md:px-[15px] md:items-center pointer-events-none z-10 col-span-6 md:col-span-12 w-full ${linksReady ? "opacity-0" : "invisible"}`}
          style={{ transform: "translateY(20px)" }}
        >
          {/* Collection Card - Container with Image and Overlapping Text (Top on Mobile) */}
          <div
            className="relative col-span-6 row-start-1 md:col-span-5 md:col-start-7 md:row-start-1 ultrawide:col-span-4 w-full"
            style={{ alignSelf: "center" }}
          >
            {/* Collection Image */}
            <Link
              href="/collection"
              prefetch={false}
              className="flex cursor-pointer transition-all duration-300 overflow-hidden pointer-events-auto"
              onClick={(e) => {
                e.preventDefault();
                animateCardsOut("/collection");
              }}
            >
              {menuImages?.collectionImage ? (
                <img
                  ref={collectionImageRef}
                  src={menuImages.collectionImage}
                  alt="Collection"
                  className="w-full md:max-w-full h-auto opacity-0"
                />
              ) : (
                <span className="text-white/50 text-sm p-24">
                  Collection Image
                </span>
              )}
            </Link>

            {/* Collection Heading and CTA - Overlapping */}
            <div
              ref={collectionTextRef}
              className="absolute md:bottom-0 flex flex-col items-end md:items-start gap-2 pointer-events-auto z-20 bottom-14 right-4 px-4 md:px-0 md:left-[15%] md:right-auto md:translate-y-1/2"
            >
              <h2 className="text-right md:text-left">
                <span
                  ref={collectionViewTheRef}
                  className="text-[#8b64e8] text-[1.6rem] md:text-3xl lg:text-4xl font-mistral block opacity-0"
                  style={{ fontStyle: "italic" }}
                >
                  view the
                </span>
                <span
                  ref={collectionHeadingRef}
                  className="text-white font-headings block -mt-2 md:-mt-2 text-[4.5rem] md:text-[4rem] lg:text-[5rem] opacity-0"
                  style={{ lineHeight: "1" }}
                >
                  Collection
                </span>
              </h2>
              <div ref={collectionCtaRef} className="opacity-0">
                <CTAButton
                  href="/collection"
                  onClick={(e) => {
                    e.preventDefault();
                    animateCardsOut("/collection");
                  }}
                >
                  View Collection
                </CTAButton>
              </div>
            </div>
          </div>

          {/* About Card - Image (Bottom Right on Mobile) */}
          <Link
            href="/about"
            prefetch={false}
            className="flex items-start justify-start md:justify-end cursor-pointer transition-all duration-300 overflow-hidden pointer-events-auto col-start-3 col-end-7 row-start-2 md:px-0 md:col-span-4 md:col-start-3 md:row-start-1 md:-translate-y-[12vh] ultrawide:col-start-4 ultrawide:col-span-3"
            onClick={(e) => {
              e.preventDefault();
              animateCardsOut("/about");
            }}
          >
            {menuImages?.aboutImage ? (
              <img
                ref={aboutImageRef}
                src={menuImages.aboutImage}
                alt="Museum"
                className="w-full h-auto opacity-0"
              />
            ) : (
              <span className="text-white/50 text-sm p-12">Museum Image</span>
            )}
          </Link>

          {/* About Card - Heading and CTA (Bottom Left Overlap on Mobile) */}
          <div
            ref={aboutTextRef}
            className="flex flex-col items-end md:items-end gap-2 pointer-events-auto col-start-1 col-end-4 row-start-2 md:px-0 md:col-span-2 md:col-start-2 md:row-start-1 md:-translate-y-[1vh] z-20 w-full ultrawide:col-start-3"
            style={{ alignSelf: "center" }}
          >
            <h2 className="text-right md:text-right">
              <span
                ref={aboutViewTheRef}
                className="text-[#8b64e8] text-[1.6rem] md:text-3xl lg:text-4xl font-mistral block opacity-0"
              >
                about the
              </span>
              <span
                ref={aboutHeadingRef}
                className="text-white font-headings block -mt-4 md:-mt-4 text-[4.5rem] md:text-[4rem] lg:text-[5rem] opacity-0"
                style={{ lineHeight: "1" }}
              >
                Museum
              </span>
            </h2>
            <div ref={aboutCtaRef} className="opacity-0">
              <CTAButton
                href="/about"
                onClick={(e) => {
                  e.preventDefault();
                  animateCardsOut("/about");
                }}
              >
                Find Out More
              </CTAButton>
            </div>
          </div>
        </div>
      </div>

      {/* Footer - fades in with cards, fixed to bottom on desktop */}
      <div ref={footerRef} className="opacity-0 -mt-[10rem] md:mt-0 md:fixed md:bottom-0 md:left-0 md:right-0 md:[&_footer]:pb-4 md:[&_footer]:mb-0 md:[&_footer]:mt-0 [&_footer_p:first-child]:hidden">
        <Footer extinctLogo={extinctLogo} />
      </div>

      <PageLoadingSpinner isLoading={isNavigating} />
    </div>
  );
}
